import {
  BarPlot,
  BarSeriesType,
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  LineSeriesType,
} from "@mui/x-charts";
import { AxisConfig, ChartsYAxisProps } from "@mui/x-charts/internals";
import { ReactElement } from "react";

function selectTicks<T>(data: T[], numTicks: number): T[] {
  return data.filter(
    (_d, idx) => idx % Math.round(data.length / numTicks) === 0
  );
}

type Series<Data extends object, YAxisId extends string> = {
  type: "line" | "bar";
  yAxisId: YAxisId;
  label: string;
  color: string;
  getValue: (d: Data) => number | null;
};

type YAxis = {
  position?: "left" | "right";
  formatValue?: (v: number) => string;
  formatTick?: (v: number) => string;
  label: string;
  width?: number;
};

export function Chart<Data extends object, YAxisId extends string>({
  data,
  series,
  getXAxisValue,
  xAxisLabel,
  xAxisTicks,
  yAxes,
  height,
}: {
  data: Data[];
  series: Series<Data, YAxisId>[];
  getXAxisValue: (d: Data) => any;
  xAxisLabel: string;
  xAxisTicks: number;
  yAxes: { [id in YAxisId]: YAxis };
  height: number;
}): ReactElement {
  return (
    <ChartDataProvider
      series={series.map(({ type, yAxisId, label, color, getValue }) => {
        return {
          type,
          yAxisId,
          label,
          color,
          data: data.map(getValue),
          valueFormatter:
            yAxes[yAxisId].formatValue ?? (value => value?.toLocaleString()),
          connectNulls: type === "line" ? true : undefined,
        } as LineSeriesType | BarSeriesType;
      })}
      height={height}
      xAxis={[
        {
          id: "xAxis",
          data: data.map(getXAxisValue),
          scaleType: "band",
          height: 40,
        },
      ]}
      yAxis={Object.keys(yAxes).map(id => {
        const { position, formatTick, width } = yAxes[id as YAxisId];
        return {
          id,
          scaleType: "linear",
          position,
          valueFormatter: formatTick ?? (value => value.toLocaleString()),
          width: width ?? 50,
          min: 0,
        } as AxisConfig<"linear", any, ChartsYAxisProps>;
      })}
    >
      <ChartsSurface>
        <ChartsAxisHighlight x="line" />
        {series.some(s => s.type === "bar") ? <BarPlot /> : null}
        {series.some(s => s.type === "line") ? <LinePlot /> : null}
        <ChartsXAxis
          label={xAxisLabel}
          axisId="xAxis"
          tickInterval={selectTicks(data.map(getXAxisValue), xAxisTicks)}
          tickPlacement="middle"
          tickLabelStyle={{ fontSize: 10 }}
        />
        {Object.keys(yAxes).map(id => (
          <ChartsYAxis
            label={yAxes[id as YAxisId].label}
            axisId={id}
            tickLabelStyle={{ fontSize: 10 }}
          />
        ))}
        <ChartsTooltip />
      </ChartsSurface>
      <ChartsLegend />
    </ChartDataProvider>
  );
}
